import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Landmark, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { copyText, fileUrl } from "@/lib/api";

const copy = async (v, what) => { (await copyText(v)) ? toast.success(`${what} കോപ്പി ചെയ്തു`) : toast.error("കോപ്പി പരാജയം — അമർത്തിപ്പിടിച്ച് കോപ്പി ചെയ്യുക"); };

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between gap-2 text-[11px]">
    <span className="text-[var(--muted-text)] shrink-0">{label}</span>
    <button type="button" data-testid={testId} onClick={() => copy(value, label)} className="font-num font-bold text-right inline-flex items-center gap-1 min-w-0"><span className="truncate">{value}</span><Copy size={11} className="shrink-0 text-[var(--muted-text)]" /></button>
  </div>
);

const UpiCard = ({ a }) => {
  const box = useRef(null);
  const link = `upi://pay?pa=${encodeURIComponent(a.upi)}&pn=${encodeURIComponent(a.payee || "RATHRI")}${a.amount ? `&am=${a.amount}` : ""}&cu=INR`;
  const download = () => {
    const canvas = box.current?.querySelector("canvas");
    if (!canvas) return;
    const out = document.createElement("canvas"); out.width = 560; out.height = 680;
    const g = out.getContext("2d"); g.fillStyle = "#fff"; g.fillRect(0, 0, 560, 680); g.drawImage(canvas, 40, 40, 480, 480);
    g.fillStyle = "#111"; g.textAlign = "center"; g.font = "bold 28px sans-serif"; g.fillText(`RATHRI - ${a.label || "UPI"}${a.amount ? ` - Rs ${a.amount}` : ""}`, 280, 565);
    g.font = "24px monospace"; g.fillText(a.upi, 280, 610);
    out.toBlob((blob) => { if (!blob) return; const url = URL.createObjectURL(blob); const el = document.createElement("a"); el.href = url; el.download = `rathri-upi-qr-${a.amount || ""}-${(a.label || "upi").replace(/\s+/g, "-").toLowerCase()}.png`; document.body.appendChild(el); el.click(); el.remove(); setTimeout(() => URL.revokeObjectURL(url), 4000); toast.success("QR ഗാലറിയിൽ സേവ് ആയി — UPI ആപ്പിൽ ഗാലറിയിൽ നിന്ന് സ്കാൻ ചെയ്യുക", { duration: 7000 }); }, "image/png");
  };
  return (
    <div data-testid="support-attach-upi" className="mt-2 rounded-xl bg-white border border-emerald-200 p-2.5 space-y-2 text-[var(--text)]">
      <p className="text-[11px] font-bold inline-flex items-center gap-1 text-emerald-800"><Smartphone size={12} /> {a.label || "UPI"} {a.amount ? `· ₹${a.amount}` : ""}</p>
      <div className="flex items-center gap-2.5">
        <div ref={box} className="rounded-lg border border-[var(--line)] p-1 bg-white shrink-0"><QRCodeCanvas value={link} size={92} level="M" includeMargin={false} /></div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <Row label="UPI ID" value={a.upi} testId="support-attach-upi-copy" />
          <a data-testid="support-attach-upi-open" href={link} className="block text-center rounded-full h-8 leading-8 text-[11px] font-bold bg-emerald-600 text-white">UPI ആപ്പിൽ അടയ്ക്കുക</a>
          <button type="button" data-testid="support-attach-qr-download" onClick={download} className="w-full btn-ghost rounded-full h-8 text-[11px] font-bold inline-flex items-center justify-center gap-1"><Download size={11} /> QR ഡൗൺലോഡ്</button>
        </div>
      </div>
    </div>
  );
};

const BankCard = ({ a }) => (
  <div data-testid="support-attach-bank" className="mt-2 rounded-xl bg-white border border-sky-200 p-2.5 space-y-1.5 text-[var(--text)]">
    <p className="text-[11px] font-bold inline-flex items-center gap-1 text-sky-800"><Landmark size={12} /> {a.bank || a.label || "ബാങ്ക്"} {a.amount ? `· ₹${a.amount}` : ""}</p>
    {a.holder && <p className="text-[10px] text-[var(--muted-text)] break-words">{a.holder}</p>}
    {a.account_number && <Row label="A/C" value={a.account_number} testId="support-attach-acct-copy" />}
    {a.ifsc && <Row label="IFSC" value={a.ifsc} testId="support-attach-ifsc-copy" />}
    {a.upi && <Row label="UPI ID" value={a.upi} testId="support-attach-bank-upi-copy" />}
  </div>
);

const ProofCard = ({ a }) => (
  <div data-testid="support-attach-proof" className="mt-2 space-y-1">
    <a data-testid="support-attach-proof-open" href={fileUrl(a.url)} target="_blank" rel="noreferrer"><img src={fileUrl(a.url)} alt="പേമെന്റ് സ്ക്രീൻഷോട്ട്" className="rounded-xl border border-[var(--line)] max-h-56 w-auto max-w-full object-contain bg-white" /></a>
    <p className="text-[10px] font-bold opacity-80">🧾 പേമെന്റ് സ്ക്രീൻഷോട്ട്{a.amount ? ` · ₹${a.amount}` : ""}{a.label ? ` · ${a.label}` : ""} — ടാപ്പ് ചെയ്താൽ വലുതാക്കാം</p>
  </div>
);

export const SupportAttachment = ({ attach }) => {
  if (!attach) return null;
  if (attach.type === "upi") return <UpiCard a={attach} />;
  if (attach.type === "bank") return <BankCard a={attach} />;
  if (attach.type === "proof") return <ProofCard a={attach} />;
  return null;
};
